import React, { useReducer, useCallback, useMemo } from 'react'
import { Indigo } from '~/types'
import { RootContext } from '~/state'
import { Recipe } from '@pollination-solutions/pollination-sdk'
import { locate } from '~/utils'

export const IndigoRoot: React.FunctionComponent<Indigo.Configuration> = ({ children, ...config }): JSX.Element => {
  // Cache root context reducer
  const reducer = useCallback((state: Indigo.Configuration, action: Indigo.Action): Indigo.Configuration => {
    switch (action.type) {
      case 'highlight-node': {
        const { key } = action

        // Do nothing if graph is not initialized
        if (!state.graph) {
          return state
        }

        // Clear highlight array if key is undefined
        if (!key) {
          state.graph.highlight = []
          return Object.assign({}, state)
        }

        // Declare highlighted nodes
        state.graph.highlight = [
          key,
          ...(state.graph?.nodes?.find((node) => node.key === key)?.dependencies ?? []),
          ...(state.graph?.nodes?.filter((node) => node.dependencies.includes(key)).map((node) => node.key) ?? []),
        ]

        return Object.assign({}, state)
      }
      case 'translate-graph': {
        const { delta } = action

        const [dx, dy] = delta
        const [x, y] = state.layout?.position ?? [0, 0]

        if (!state.layout) {
          return state
        }

        state.layout.position = [x + dx, y + dy]

        return { ...state }
      }
      case 'set-zoom': {
        const { zoom: z } = action

        const min = 50
        const max = 150

        const zoom = z < min ? min : z > max ? max : z

        if (!state.layout) {
          return state
        }

        state.layout.zoom = zoom / 100

        return { ...state }
      }
      case 'set-flow': {
        const { name } = action

        if (!state.meta || !state.meta.recipe) {
          return state
        }

        const { main, subFlows } = locate.recipeEntrypoint(state.meta.recipe.flow)

        const active = name === 'main' ? main : subFlows.find((flow) => flow.name === name)

        if (!active) {
          console.error(`Could not locate sub-flow ${name}.`)
          return state
        }

        state.meta.activeFlow = active
        state.graph = { nodes: [], edges: [], highlight: [] }

        return { ...state }
      }
      case 'register-node': {
        const { key, ref, dependencies } = action

        // Add node to registry
        state.graph?.nodes?.push({ key: key, ref: ref, dependencies: dependencies ?? [] })

        // Add edges to registry
        dependencies?.forEach((dep) => {
          state.graph?.edges?.push({ from: dep, to: key })
        })

        return { ...state }
      }
      case 'register-root': {
        const { ref } = action

        if (!state.layout) {
          return state
        }

        // Left and right have been set, apply centering translation
        if (state.layout.left && state.layout.right) {
          state.layout.root = ref

          const root = state.layout.root.current
          const left = state.layout.left.current
          const right = state.layout.right.current

          if (!root || !left || !right) {
            console.log(`Missing element: ${root} / ${left} / ${right}`)
            return state
          }

          const { x: tx, width, height } = root.getBoundingClientRect()
          const { x: lx, height: hl } = left.getBoundingClientRect()
          const { x: rx, width: wr, height: hr } = right.getBoundingClientRect()

          const graphWidth = rx + wr - lx - tx
          const graphHeight = Math.max(hl, hr)

          const dx = 0.5 * (width - graphWidth)
          const dy = 0.5 * (height - graphHeight)

          state.layout.position = [dx, dy]
          state.layout.center = [dx, dy]

          return { ...state }
        }

        return { ...state, layout: { ...(state.layout ?? { direction: 'LR', position: [0, 0] }), root: ref } }
      }
      case 'register-extent': {
        const { ref, position } = action

        if (!state.layout) {
          return state
        }

        switch (position) {
          case 'left': {
            state.layout.left = ref
            break
          }
          case 'right': {
            state.layout.right = ref
            break
          }
        }

        // Left and right have been set, apply centering translation
        if (state.layout.left && state.layout.right && state.layout.root) {
          const root = state.layout.root.current
          const left = state.layout.left.current
          const right = state.layout.right.current

          if (!root || !left || !right) {
            console.log(`Missing element: ${root} / ${left} / ${right}`)
            return state
          }

          const { x: tx, width, height } = root.getBoundingClientRect()
          const { x: lx, height: hl } = left.getBoundingClientRect()
          const { x: rx, width: wr, height: hr } = right.getBoundingClientRect()

          const graphWidth = rx + wr - lx - tx
          const graphHeight = Math.max(hl, hr)

          const dx = 0.5 * (width - graphWidth)
          const dy = 0.5 * (height - graphHeight)

          state.layout.position = [dx, dy]
          state.layout.center = [dx, dy]

          return { ...state }
        }

        return { ...state }
      }
      case 'register-recipe': {
        const { recipe } = action

        if (!state.meta) {
          state.meta = {}
        }

        state.meta.recipe = recipe
        state.meta.activeFlow = recipe.flow.find((flow) => flow.name === 'main')

        return { ...state }
      }
    }
  }, [])

  // Create default configuration
  const defaultConfig: Indigo.Configuration = useMemo(() => {
    return {
      meta: {
        domain: 'https://app.pollination.cloud',
      },
      layout: {
        direction: 'LR',
        position: [0, 0],
        center: [0, 0],
        zoom: 1,
      },
      graph: {
        nodes: [],
        edges: [],
        highlight: [],
      },
    }
  }, [])

  // Pass reducer and any user-provided configuration to initial state
  const [state, act] = useReducer(reducer, { ...defaultConfig, ...config })

  // Merge state and public actions into context object
  const dispatch: Indigo.Dispatch = {
    highlight: (key: string | undefined) => act({ type: 'highlight-node', key: key }),
    pan: (dx: number, dy: number) => act({ type: 'translate-graph', delta: [dx, dy] }),
    setZoom: (zoom: number) => act({ type: 'set-zoom', zoom: zoom }),
    setFlow: (name: string) => act({ type: 'set-flow', name: name }),
    register: (key: string, ref: React.RefObject<HTMLDivElement>, dependencies?: string[]) =>
      act({ type: 'register-node', key: key, ref: ref, dependencies: dependencies }),
    registerRecipe: (recipe: Recipe) => act({ type: 'register-recipe', recipe: recipe }),
    registerRoot: (ref: React.RefObject<HTMLDivElement>) => act({ type: 'register-root', ref: ref }),
    registerExtent: (ref: React.RefObject<HTMLDivElement>, position: 'left' | 'right') =>
      act({ type: 'register-extent', ref: ref, position: position }),
  }

  return (
    <RootContext.Provider value={{ ...state, ...dispatch }}>
      {children}
      <style global jsx>{`
        @import url('https://fonts.googleapis.com/css2?family=Space+Mono&display=swap');
        @import url('https://fonts.googleapis.com/css2?family=Inter&display=swap');
      `}</style>
    </RootContext.Provider>
  )
}
